import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { contactAPI } from '../../services/api';

const EMPTY_FORM = { name: '', company: '', email: '', phone: '', service: '', message: '', consent: false };

const Field = ({ id, label, required, ...props }) => (
  <div>
    <Label htmlFor={id}>{label}{required && ' *'}</Label>
    <Input id={id} required={required} className="mt-2" data-testid={`contact-${id}`} {...props} />
  </div>
);

export const ContactForm = () => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.consent) return toast.error('Please agree to the privacy policy');
    try {
      const { consent, ...submitData } = formData;
      await contactAPI.submit(submitData);
      toast.success('Thank you! We will contact you soon.');
      setFormData(EMPTY_FORM);
    } catch {
      toast.error('Failed to submit. Please try again.');
    }
  };

  return (
    <div className="bg-gray-50 rounded-2xl p-8 shadow-lg">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field id="name" label="Full Name" required type="text" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} placeholder="John Doe" />
          <Field id="company" label="Company" type="text" value={formData.company} onChange={(e) => handleChange('company', e.target.value)} placeholder="Company Name" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field id="email" label="Email" required type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="john@example.com" />
          <Field id="phone" label="Phone" type="tel" value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} placeholder="+91 XXXXXXXXXX" />
        </div>
        <div>
          <Label htmlFor="service">Service Interest</Label>
          <Select value={formData.service} onValueChange={(value) => handleChange('service', value)}>
            <SelectTrigger className="mt-2" data-testid="contact-service">
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="renewable">Renewable Energy Consulting</SelectItem>
              <SelectItem value="regulatory">Regulatory Consultancy</SelectItem>
              <SelectItem value="legal">Legal Advisory</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="message">Message *</Label>
          <Textarea id="message" value={formData.message} onChange={(e) => handleChange('message', e.target.value)} required className="mt-2 min-h-32" placeholder="Tell us about your project or requirements..." data-testid="contact-message" />
        </div>
        <div className="flex items-start gap-3">
          <Checkbox id="consent" checked={formData.consent} onCheckedChange={(checked) => handleChange('consent', checked)} data-testid="contact-consent" />
          <Label htmlFor="consent" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
            I agree to the privacy policy and consent to SOLARIAN contacting me regarding my inquiry
          </Label>
        </div>
        <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-6 text-lg" data-testid="contact-submit">
          <Send className="w-5 h-5 mr-2" /> Send Message
        </Button>
      </form>
    </div>
  );
};
